import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { getIso31662dbCountries } from "../../../../../../dynamic-packages";
import { _t } from "../../../../../../utils/I18nUtils";
import "./PaymentRequestForm.scss";
import {isEmailValid} from "../../../../../../utils/ValidationUtils";
import GlobalConst from "../../../../../../constants/GlobalConst";

const listOfCountries = ['US', 'CA', 'MX'];
const trKey = "components.addFundsForm.billing";

const PaymentRequestForm = ({ vm, style }) => {
    const [isoDb, setIsoDb] = useState({ countries: [] });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getIso31662dbCountries().then((iso31662db) => {
            const favISO = GlobalConst.SITE_TLD === ".mx" ? "MX" : "US";
            const filteredCountries = iso31662db.countries.filter((c) => listOfCountries.includes(c.iso))
                                                          .sort((a, b) => {
                if (a.iso === favISO) return -1;
                if (b.iso === favISO) return 1;
                return a.name.localeCompare(b.name);
            });
            setIsoDb({
                listOfCountries: iso31662db.listOfCountries,
                countries: filteredCountries });

            //assing country and state code if needed, handle default country & state, and case where there is country by no code etc..
            if (!vm.billingAddress.country && !vm.billingAddress.countryCode && filteredCountries.length > 0) {
                const defaultCountry = filteredCountries[0];
                vm.setBillingAddress({
                    ...vm.billingAddress,
                    country: defaultCountry.name,
                    countryCode: defaultCountry.iso,
                    state: defaultCountry.regions?.[0]?.name ?? "",
                    stateCode: defaultCountry.regions?.[0]?.iso ?? "",
                });
            }
            else if(vm.billingAddress.country && !vm.billingAddress.countryCode && vm.billingAddress.state && !vm.billingAddress.stateCode) {
                const country = filteredCountries.find(c => c.name.toLowerCase() === vm.billingAddress.country.toLowerCase());
                const state = vm.billingAddress.state ? country?.regions?.find(c => c.name.toLowerCase() === vm.billingAddress.state.toLowerCase()) : null;
                const countryCode = country ? country.iso : null;
                const stateCode = state ? state.iso : null;

                if (country) {
                    vm.setBillingAddress({
                        ...vm.billingAddress,
                        countryCode: countryCode || "",
                        stateCode : stateCode || ""
                    });
                }
            }
        });
    }, []);

    const hasErrorsWithAddress = errors.Address1 || errors.ZipCode || errors.City || errors.Country || errors.State;

    const validate = () => {
        let newErrors = {};

        if (!vm.paymentRequest.Email) newErrors.Email = "* Email is required";
        else if(!isEmailValid(vm.paymentRequest.Email)) newErrors.Email = "* Email is invalid";
        if (!vm.paymentRequest.FirstName) newErrors.FirstName = "* First Name is required";
        if (!vm.paymentRequest.LastName) newErrors.LastName = "* Last Name is required";
        if (!vm.billingAddress.address1) newErrors.Address1 = "* Address is required";
        if (!vm.billingAddress.zipCode) newErrors.ZipCode = "* Postal Code is required";
        if (!vm.billingAddress.city) newErrors.City = "* City is required";
        if (!vm.billingAddress.country) newErrors.Country = "* Country is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        vm.setPaymentRequest({ ...vm.paymentRequest, [field]: value });
    };
    const handleCountryChange = (e) => {
        const selectedCountry = isoDb.countries.find((x) => x.iso === e.target.value);
        if (selectedCountry) {
            vm.setBillingAddress({
                ...vm.billingAddress,
                country: selectedCountry.name,
                countryCode: selectedCountry.iso,
                state: selectedCountry.regions?.[0]?.name ?? "",
                stateCode: selectedCountry.regions?.[0]?.iso ?? "",
            });
        }
    };
    const handleStateChange = (e) => {
        const selectedRegion = isoDb?.listOfCountries?.[vm.billingAddress.countryCode]?.regions?.find(
            (x) => x.iso === e.target.value
        );
        vm.setBillingAddress({
            ...vm.billingAddress,
            "state": selectedRegion?.name || "",
            "stateCode": selectedRegion?.iso || ""
        });
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            vm.handlePaymentRequestSubmitClick();
        }
    };

    if(!isoDb.countries || isoDb.countries.length === 0) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="PaymentRequestForm" style={style}>
            <h3>{_t("views.addFundView.paymentRequestForm.title")}</h3>
            <div className="row">
                <div className="col-md-6">
                    <div className="form-group">
                        <label>{_t(`${trKey}.firstNameLabel`)}</label>
                        <input
                            type="text"
                            style={{width: "100%"}}
                            onChange={(e) => handleInputChange("FirstName", e.target.value)}
                            value={vm.paymentRequest.FirstName || ""}
                        />
                        {errors.FirstName && <div className="error">{errors.FirstName}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label>{_t(`${trKey}.lastNameLabel`)}</label>
                        <input
                            type="text"
                            style={{width: "100%"}}
                            onChange={(e) => handleInputChange("LastName", e.target.value)}
                            value={vm.paymentRequest.LastName || ""}
                        />
                        {errors.LastName && <div className="error">{errors.LastName}</div>}
                    </div>
                </div>

            </div>
            <div className="row">
                <div className="col-md-6">
                    <div className="form-group">
                        <label>{_t(`${trKey}.emailLabel`)}</label>
                        <input
                            type="text"
                            style={{width: "100%"}}
                            onChange={(e) => handleInputChange("Email", e.target.value)}
                            value={vm.paymentRequest.Email || ""}
                        />
                        {errors.Email && <div className="error">{errors.Email}</div>}
                    </div>
                </div>

            </div>

            <div className="row">
                <div className="col-md-6">
                    <div className="checkbox">
                        <input type="checkbox"
                               id={"isCustom"}
                               checked={!vm.editBillingAddress}
                               onChange={() => {
                                   vm.setEditBillingAddress(!vm.editBillingAddress);
                               }}/>
                        <label htmlFor="isCustom">{_t(`${trKey}.sameAsHomeAddressLabel`)}</label>
                    </div>
                </div>
            </div>

            {(!vm.editBillingAddress) &&
                <div className="row">
                    <div className="col-xs-12">
                        <div className="address">
                            {vm.billingAddress.address1 != "" ? <div>{vm.billingAddress.address1}</div> : null}
                            {vm.billingAddress.address2 != "" ? <div>{vm.billingAddress.address2}</div> : null}
                            <div>
                                {vm.billingAddress.city != "" ?
                                    <span>{vm.billingAddress.city}&nbsp;</span> : null}
                                {vm.billingAddress.state != "" ?
                                    <span>{vm.billingAddress.state}&nbsp;</span> : null}
                                {vm.billingAddress.zipCode != "" ?
                                    <span>{vm.billingAddress.zipCode}</span> : null}</div>
                            <div>
                                {vm.billingAddress.country != "" ?
                                    <span>{vm.billingAddress.country}</span> : null}</div>
                        </div>
                    </div>
                </div>}
            {(vm.editBillingAddress || hasErrorsWithAddress) && <React.Fragment>
                <div className="customBillingAddress">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>{_t(`${trKey}.countryLabel`)}</label>
                                <select
                                    value={
                                        isoDb?.countries?.find(
                                            (x) =>
                                                x.name.toLowerCase() === vm.billingAddress?.country?.toLowerCase() ||
                                                x.iso === vm.billingAddress.countryCode
                                        )?.iso || ""
                                    }
                                    onChange={handleCountryChange}
                                >
                                    <option key={"none"} value={""}>-- Select --</option>
                                    {isoDb?.countries?.map((country) => (
                                        <option key={country.iso} value={country.iso}>
                                            {country.name}
                                        </option>
                                    ))}
                                </select>

                                {errors.Country && <div className="error">*</div>}
                            </div>
                        </div>

                        {vm.billingAddress.countryCode && (isoDb?.listOfCountries?.[vm.billingAddress.countryCode]?.regions?.length > 0) &&
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label>{_t(`${trKey}.stateLabel`)}</label>
                                    <select
                                        value={
                                            isoDb?.listOfCountries?.[vm.billingAddress.countryCode]?.regions?.find(
                                                (x) =>
                                                    x.name.toLowerCase() === vm.billingAddress.state.toLowerCase() ||
                                                    x.iso === vm.billingAddress.stateCode
                                            )?.iso || ""
                                        }
                                        onChange={handleStateChange}
                                    >
                                        <option key={"none"} value={""}>-- Select --</option>
                                        {isoDb?.listOfCountries?.[vm.billingAddress.countryCode]?.regions?.map((region) => (
                                            <option key={region.iso} value={region.iso}>
                                                {region.name}
                                            </option>
                                        ))}
                                    </select>

                                    {errors.State && <div className="error">*</div>}
                                </div>
                            </div>}
                    </div>
                    <div className="row">
                        <div className="col-xs-12 col-sm-6">
                            <div className="form-group">
                                <label>{_t(`${trKey}.addressLabel`)}</label>
                                <input type="text" style={{width: '100%'}}
                                       onChange={(e) => {
                                           vm.setBillingAddress({
                                               ...vm.billingAddress,
                                               address1: e.target.value
                                           });
                                       }}
                                       name="Address"
                                       value={vm.billingAddress.address1}/>
                                {errors.Address1 && <div className="error">*</div>}
                            </div>
                        </div>
                        <div className="col-xs-12 col-sm-6">
                            <div className="form-group">
                                <label>{_t(`${trKey}.cityLabel`)}</label>
                                <input type="text" style={{width: '100%'}}
                                       onChange={(e) => {
                                           vm.setBillingAddress({...vm.billingAddress, city: e.target.value});
                                       }}
                                       value={vm.billingAddress.city}/>
                                {errors.City && <div className="error">*</div>}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>{_t(`${trKey}.zipCodeLabel`)}</label>
                                <input type="text"
                                       style={{width: '100%'}}
                                       onChange={(e) => {
                                           vm.setBillingAddress({...vm.billingAddress, zipCode: e.target.value})
                                       }}
                                       values={vm.billingAddress.zipCode}/>
                                {errors.ZipCode && <div className="error">*</div>}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>{_t(`${trKey}.phoneLabel`)}</label>
                                <input type="text" style={{width: '100%'}}
                                       onChange={(e) => {
                                           vm.setBillingAddress({...vm.billingAddress, phone1: e.target.value});
                                       }}
                                       value={vm.billingAddress.phone1}/>
                                {errors.Phone1 && <div className="error">*</div>}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12">
                            <div className="checkbox">
                                <input type="checkbox"
                                       id={"saveAsHomeAddress"}
                                       checked={vm.saveAsHomeAddress}
                                       onChange={e => {
                                           vm.setSaveAsHomeAddress(!vm.saveAsHomeAddress);
                                       }}
                                />
                                <label
                                    htmlFor="saveAsHomeAddress">{_t(`${trKey}.saveAsHomeAddressLabel`)}</label>

                            </div>
                        </div>
                    </div>
                </div>
                <br/><br/>
            </React.Fragment>}

            <div className="btnContainer">
                <button className="btn btn-primary"
                        onClick={handleSubmit}>
                    {_t("views.addFundsView.payRetailers.customerInfoForm.continue")}
                </button>
                <div className="skrill-logo-btn"></div>
            </div>
        </div>
    );
};

PaymentRequestForm.propTypes = {
    vm: PropTypes.object.isRequired,
};

export default PaymentRequestForm;
